import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { buildApiUrl } from "../utils/urlHelpers";
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import SeoMetaTags from "../components/SeoMetaTags";

function SinglePost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch single post data when component mounts or ID changes
    if (!id) return;

    setLoading(true);
    const apiUrl = buildApiUrl(`wp/v2/posts/${id}?_embed`);
    
    fetch(apiUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Post not found (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        setPost(data);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        console.error("Error fetching post:", err);
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  // Display loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Display error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  // Display post not found
  if (!post) {
    return (
      <Alert severity="warning" sx={{ mt: 4 }}>
        Post not found.
      </Alert>
    );
  }

  // Format the published date
  const publishDate = new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Get featured image if available
  const featuredImage =
    post._embedded &&
    post._embedded["wp:featuredmedia"] &&
    post._embedded["wp:featuredmedia"][0]
      ? post._embedded["wp:featuredmedia"][0].source_url
      : null;

  // Get author if available
  const author =
    post._embedded && post._embedded.author && post._embedded.author[0]
      ? post._embedded.author[0].name
      : "Unknown Author";

  return (
    <Box sx={{ my: 4 }}>
      <SeoMetaTags
        title={post?.title?.rendered ? `${post.title.rendered} | Sentry Law` : "Blog Post | Sentry Law"}
        description={post?.excerpt?.rendered ? post.excerpt.rendered.replace(/<[^>]*>/g, '').substring(0, 160) : "Read our latest legal insights and articles from Sentry Law."}
        slug={`post/${id}`}
      />


      <Paper sx={{ p: 4 }}>
        {/* Post title */}
        <Typography variant="h4" component="h1" gutterBottom>
          <span dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
        </Typography>

        {/* Post meta information */}
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          By {author} • Published on {publishDate}
        </Typography>

        {/* Featured image if available */}
        {featuredImage && (
          <Box
            component="img"
            src={featuredImage}
            alt={post.title.rendered}
            sx={{
              width: "100%",
              maxHeight: "500px",
              objectFit: "cover",
              my: 2,
              borderRadius: 1,
            }}
          />
        )}

        <Divider sx={{ my: 3 }} />

        {/* Post content */}
        <Box
          sx={{
            "& img": { maxWidth: "100%", height: "auto" },
            "& a": { color: "primary.main" },
            "& h2, & h3, & h4, & h5, & h6": { mt: 4, mb: 2 },
            "& p": { mb: 2, lineHeight: 1.7 },
            "& ul, & ol": { mb: 2, pl: 2 },
          }}
        >
          <Typography component="div">
            <div dangerouslySetInnerHTML={{ __html: post.content.rendered }} />
          </Typography>
        </Box>

        {/* Post tags and categories */}
        {post._embedded && post._embedded["wp:term"] && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Categories:
              {post._embedded["wp:term"][0]?.map((category, index) => (
                <Link
                  key={category.id}
                  href={`/category/${category.slug}`}
                  sx={{ ml: 1 }}
                >
                  {category.name}
                  {index < post._embedded["wp:term"][0].length - 1 ? "," : ""}
                </Link>
              ))}
            </Typography>

            {post._embedded["wp:term"][1]?.length > 0 && (
              <Typography variant="subtitle2">
                Tags:
                {post._embedded["wp:term"][1]?.map((tag, index) => (
                  <Link key={tag.id} href={`/tag/${tag.slug}`} sx={{ ml: 1 }}>
                    {tag.name}
                    {index < post._embedded["wp:term"][1].length - 1 ? "," : ""}
                  </Link>
                ))}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default SinglePost;
